import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Play, ExternalLink, RefreshCw } from "lucide-react";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import {
  listWinsRuns,
  getWinsRunScreenings,
  runWinsIngestNow,
  REJECT_REASON_LABELS,
  OUTCOME_LABELS,
  type WinsRun,
} from "@/api/wins";

/**
 * The wins pipeline, from the operator's side.
 *
 * Nobody curates this feed, so this page answers the only three questions
 * anybody can usefully ask about it: is it running, is the rule right, and can
 * I make it run now.
 *
 * There is no approve/reject/edit here, and that is the design rather than an
 * omission. A queue nobody empties is the failure the whole pipeline avoids;
 * putting one behind an admin login reintroduces it wearing a badge. A bad
 * item gets retired from the announcements desk like any other row.
 */

const fmt = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

function outcomeBadge(run: WinsRun) {
  if (run.outcome !== "ok") {
    return <Badge variant="red">{OUTCOME_LABELS[run.outcome] ?? run.outcome}</Badge>;
  }
  // A run can succeed with dead feeds in it. Normal on the public internet,
  // and still worth seeing — invisible partial failure is how six sources
  // become one without anybody noticing.
  if (run.sourcesFailed > 0) {
    return <Badge variant="yellow">ok · {run.sourcesFailed} source(s) failed</Badge>;
  }
  return <Badge variant="green">ok</Badge>;
}

export default function AdminWinsLab() {
  const qc = useQueryClient();
  const [openRun, setOpenRun] = useState<string | null>(null);
  const [verdict, setVerdict] = useState<"reject" | "accept" | "all">("reject");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["wins-runs"],
    queryFn: () => listWinsRuns(20),
  });

  const { data: screenings, isLoading: loadingScreenings } = useQuery({
    queryKey: ["wins-screenings", openRun, verdict],
    queryFn: () =>
      getWinsRunScreenings(openRun!, verdict === "all" ? undefined : verdict),
    enabled: !!openRun,
  });

  const state = data?.data;
  const runs = state?.runs ?? [];

  async function runNow() {
    // It publishes. Say so in those words rather than "are you sure?".
    const ok = window.confirm(
      "Run the wins ingest now?\n\n" +
        "This publishes for real — there is no rehearsal mode, because a dry " +
        "run that skipped the write would not test the part most likely to " +
        "break. It also runs even if the schedule is switched off.",
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await runWinsIngestNow();
      const s = res.data as { published?: number; screened?: number };
      setNotice(`Run finished — screened ${s.screened ?? 0}, published ${s.published ?? 0}.`);
      qc.invalidateQueries({ queryKey: ["wins-runs"] });
      qc.invalidateQueries({ queryKey: ["admin-announcements"] });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Run failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Wins pipeline"
        subtitle="Reads news feeds, screens them against the editorial rule, publishes what survives. Nobody reviews it, so this is where you check that it is behaving."
        breadcrumbs={[
          { label: "Lab", href: "/" },
          { label: "Admin", href: "/admin" },
          { label: "Wins pipeline" },
        ]}
      />

      {/* ── Is it on? ───────────────────────────────────────────────
          First thing on the page, because a switched-off pipeline and a
          broken one produce exactly the same empty table. */}
      {state && (
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          <span className="text-sm font-medium text-gray-700">Schedule</span>
          {state.enabled ? (
            <Badge variant="green">on — twice daily</Badge>
          ) : (
            <Badge variant="gray">off</Badge>
          )}
          <span className="ml-3 text-sm font-medium text-gray-700">Screening</span>
          {state.screening === "stub" ? (
            <Badge variant="yellow">stub — publishes nothing</Badge>
          ) : (
            <Badge variant="blue">{state.screening}</Badge>
          )}
          <div className="ml-auto flex gap-2">
            <Button variant="secondary" onClick={() => qc.invalidateQueries({ queryKey: ["wins-runs"] })}>
              <RefreshCw className="mr-1.5 h-4 w-4" /> Refresh
            </Button>
            <Button onClick={runNow} disabled={busy}>
              <Play className="mr-1.5 h-4 w-4" /> {busy ? "Running…" : "Run now"}
            </Button>
          </div>
        </div>
      )}

      {/* Both switches have to be right before a single item can reach a
          member. Saying which one is holding it saves the guess. */}
      {state && (!state.enabled || state.screening === "stub") && (
        <p className="mb-6 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Nothing publishes on a schedule right now.
          {!state.enabled && " The schedule is off."}
          {state.screening === "stub" &&
            " Screening is on the stand-in, which accepts nothing by design — no model, no unattended publishing."}{" "}
          <b>Run now</b> still works and still publishes.
        </p>
      )}

      {notice && (
        <p className="mb-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">{notice}</p>
      )}
      {error && <p className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}

      {/* ── Runs ─────────────────────────────────────────────────── */}
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : runs.length === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500">
          No runs yet. {state?.enabled ? "The first scheduled run has not happened." : "The schedule is off — press Run now to try it."}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2.5 font-medium">Started</th>
                <th className="px-4 py-2.5 font-medium">Outcome</th>
                <th className="px-4 py-2.5 text-right font-medium">Fetched</th>
                <th className="px-4 py-2.5 text-right font-medium">Dupes</th>
                <th className="px-4 py-2.5 text-right font-medium">Screened</th>
                <th className="px-4 py-2.5 text-right font-medium">Published</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {runs.map((r) => (
                <tr key={r.id} className={openRun === r.id ? "bg-blue-50/40" : undefined}>
                  <td className="whitespace-nowrap px-4 py-2.5 text-gray-700">{fmt(r.startedAt)}</td>
                  <td className="px-4 py-2.5">{outcomeBadge(r)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{r.fetched}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">
                    {r.droppedUrl + r.droppedStory}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{r.screened}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-medium text-gray-900">
                    {r.published}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      className="text-xs font-medium text-blue-700 hover:underline"
                      onClick={() => setOpenRun(openRun === r.id ? null : r.id)}
                    >
                      {openRun === r.id ? "Hide" : "Screenings"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Screenings ───────────────────────────────────────────────
          Defaults to REJECTED, which is the whole reason this view exists.
          A count says eleven things were refused; only the headlines say
          whether refusing them was right. */}
      {openRun && (
        <div className="mt-6">
          <div className="mb-3 flex items-center gap-2">
            <h2 className="text-sm font-semibold text-gray-900">What this run ruled on</h2>
            <div className="ml-auto flex gap-1">
              {(["reject", "accept", "all"] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => setVerdict(v)}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium ${
                    verdict === v
                      ? "bg-gray-900 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {v === "reject" ? "Turned away" : v === "accept" ? "Carried" : "All"}
                </button>
              ))}
            </div>
          </div>

          <p className="mb-3 text-xs text-gray-500">
            Turned away is the default on purpose — a wrong refusal here belongs in the eval bank
            the same day it appears.
          </p>

          {loadingScreenings ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (screenings?.data.screenings.length ?? 0) === 0 ? (
            <p className="rounded-lg border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500">
              Nothing recorded for this filter.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
              {screenings!.data.screenings.map((s) => (
                <li key={s.id} className="px-4 py-3">
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900">{s.title}</p>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {s.source}
                        {s.confidence != null && ` · confidence ${s.confidence.toFixed(2)}`}
                        {s.modelVersion && ` · ${s.modelVersion}`}
                      </p>
                    </div>
                    <div className="flex flex-none items-center gap-2">
                      {s.verdict === "accept" ? (
                        <Badge variant="green">{s.topic ?? "carried"}</Badge>
                      ) : (
                        <Badge variant="gray">
                          {s.reason ? REJECT_REASON_LABELS[s.reason] ?? s.reason : "turned away"}
                        </Badge>
                      )}
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-gray-700"
                        title="Open the source"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
